import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PEOPLE_ADMIN_ROLES, type Role } from '@cfl/shared';

import { api, ApiError } from '@/lib/api';
import { peopleKeys, useDepartments, useDesignations, type DepartmentNode, type Designation } from '@/lib/people-api';

/**
 * Department and designation mutations.
 *
 * Every endpoint returns the full collection, so the cache is replaced from the
 * response rather than invalidated and refetched - one round trip instead of two,
 * and the tree can never briefly render a stale parent.
 */

function useSetDepartments() {
  const queryClient = useQueryClient();
  return (next: DepartmentNode[]) => {
    queryClient.setQueryData(peopleKeys.departments, next);
    // Employee rows embed department names, so they go stale on a rename.
    void queryClient.invalidateQueries({ queryKey: ['people'] });
  };
}

export function useCreateDepartment() {
  const set = useSetDepartments();
  return useMutation<DepartmentNode[], ApiError, { name: string; parentId?: string | null }>({
    mutationFn: (body) => api.post<DepartmentNode[]>('/departments', body),
    onSuccess: set,
  });
}

export function useUpdateDepartment() {
  const set = useSetDepartments();
  return useMutation<
    DepartmentNode[],
    ApiError,
    { id: string; name?: string; parentId?: string | null; headId?: string | null }
  >({
    mutationFn: ({ id, ...body }) => api.patch<DepartmentNode[]>(`/departments/${id}`, body),
    onSuccess: set,
  });
}

export function useDeleteDepartment() {
  const set = useSetDepartments();
  return useMutation<DepartmentNode[], ApiError, string>({
    mutationFn: (id) => api.delete<DepartmentNode[]>(`/departments/${id}`),
    onSuccess: set,
  });
}

function useSetDesignations() {
  const queryClient = useQueryClient();
  return (next: Designation[]) => {
    queryClient.setQueryData(peopleKeys.designations, next);
    void queryClient.invalidateQueries({ queryKey: ['people'] });
  };
}

export function useCreateDesignation() {
  const set = useSetDesignations();
  return useMutation<Designation[], ApiError, { title: string }>({
    mutationFn: (body) => api.post<Designation[]>('/designations', body),
    onSuccess: set,
  });
}

/**
 * Rename a job title.
 *
 * A rename, not a replacement: every employee holding it keeps holding it, and a KRA
 * group pinned to it stays pinned. That is why renaming is safe to offer where deleting
 * is refused the moment anybody holds the title — the API rejects a duplicate title, so
 * two designations can never collapse into one by being typed the same.
 */
export function useUpdateDesignation() {
  const set = useSetDesignations();
  return useMutation<Designation[], ApiError, { id: string; title: string }>({
    mutationFn: ({ id, ...body }) => api.patch<Designation[]>(`/designations/${id}`, body),
    onSuccess: set,
  });
}

export function useDeleteDesignation() {
  const set = useSetDesignations();
  return useMutation<Designation[], ApiError, string>({
    mutationFn: (id) => api.delete<Designation[]>(`/designations/${id}`),
    onSuccess: set,
  });
}

/* ------------------------------------------------------------------------- *
 * Inline creation from a picker
 *
 * Both endpoints answer with the whole collection, which is what makes creating
 * from a dropdown work at all: the new row comes back in the same response, so it
 * can be selected without a second round trip or a "now find it in the list again".
 *
 * Each returns the id to select. A 409 is treated as success, not as an error —
 * two people onboarding at once will both type "Customer Success", and the second
 * one wants the department that now exists, not a red toast about a race they did
 * not know they were in.
 * ------------------------------------------------------------------------- */

/** Whether this user may add departments and designations — mirrors `@Roles` on the API. */
export function canManageOrgStructure(role: Role): boolean {
  return PEOPLE_ADMIN_ROLES.includes(role);
}

function sameName(a: string, b: string): boolean {
  return a.trim().toLowerCase().replace(/\s+/g, ' ') === b.trim().toLowerCase().replace(/\s+/g, ' ');
}

function findDepartment(nodes: DepartmentNode[], name: string): DepartmentNode | undefined {
  for (const node of nodes) {
    if (sameName(node.name, name)) return node;
    const hit = findDepartment(node.children, name);
    if (hit) return hit;
  }
  return undefined;
}

export function useInlineCreateDepartment(): (name: string) => Promise<string> {
  const create = useCreateDepartment();
  const { data: current } = useDepartments();

  return async (name: string) => {
    try {
      const next = await create.mutateAsync({ name: name.trim() });
      const made = findDepartment(next, name);
      if (!made) throw new Error(`"${name}" was created but did not come back in the list.`);
      return made.id;
    } catch (e) {
      const existing =
        e instanceof ApiError && e.status === 409 ? findDepartment(current ?? [], name) : undefined;
      if (existing) return existing.id;
      throw e;
    }
  };
}

export function useInlineCreateDesignation(): (title: string) => Promise<string> {
  const create = useCreateDesignation();
  const { data: current } = useDesignations();

  return async (title: string) => {
    const match = (list: Designation[]) => list.find((d) => sameName(d.title, title));
    try {
      const next = await create.mutateAsync({ title: title.trim() });
      const made = match(next);
      if (!made) throw new Error(`"${title}" was created but did not come back in the list.`);
      return made.id;
    } catch (e) {
      const existing = e instanceof ApiError && e.status === 409 ? match(current ?? []) : undefined;
      if (existing) return existing.id;
      throw e;
    }
  };
}
